import { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

import type { ReactElement } from 'react';
import type { Editor, FontType } from '../types';

import styles from './DropDownFont.module.scss';
import { FontsList } from './FontsList';

type DropDownFontPropsType = {
  fonts: FontType[];
  editor: Editor;
  icon: ReactElement;
};

export const DropDownFont: React.FC<DropDownFontPropsType> = ({ fonts, editor, icon }) => {
  const [fontNames, setFontsNames] = useState<FontType[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const iconRef = useRef(null);
  const dropdownRef = useRef(null);
  const selectedFont = editor.getAttributes('textStyle').fontFamily || '';

  const handleSelect = (item: FontType) => {
    editor.chain().focus().setFontFamily(item.value).run();
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    if (isDropdownOpen && iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isDropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !iconRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleIconClick = useCallback(() => {
    if (isDropdownOpen) {
      setIsDropdownOpen(false);
    } else {
      setFontsNames(fonts);
      setIsDropdownOpen(true);
    }
  }, [isDropdownOpen, fonts]);

  return (
    <div className={styles.dropdownFont}>
      <div ref={iconRef} onClick={handleIconClick}>
        {icon}
      </div>

      {isDropdownOpen &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
            }}
            className={styles.fontListWrapper}
          >
            <FontsList items={fontNames} onSelect={handleSelect} selectedFont={selectedFont} />
          </div>,
          document.body,
        )}
    </div>
  );
};
